import { z } from 'zod';

import { AGENT_NOTE_RANGE_PRESETS } from '@apps/web/features/admin_agent_notes/model/range_presets';
import {
    type ApiQueryParamsType,
    type components,
} from '@libraries/typescript/api_client';

type AgentNoteStatus = components['schemas']['AgentNoteStatus'];
type AgentNoteSource = components['schemas']['AgentNoteSource'];
type AgentNoteDomain = components['schemas']['AgentNoteDomain'];
type ReviewItemState = components['schemas']['ReviewItemState'];
type AgentNotesListRating = NonNullable<
    NonNullable<ApiQueryParamsType<'/admin/agent-notes', 'get'>>['rating']
>;

export const AGENT_NOTE_TABS = [
    'notes',
    'dashboard',
    'held_items',
    'dry_runs',
    'memory_backfill',
    'sync_backfill',
] as const;
export type AgentNotesTab = (typeof AGENT_NOTE_TABS)[number];

export const AGENT_NOTE_STATUSES = [
    'pending',
    'classified',
    'processed',
    'partially_processed',
    'fallback',
    'failed',
] as const satisfies ReadonlyArray<AgentNoteStatus>;

// The sources and domains the pipeline still writes. The generated unions
// may carry retired values until the database drops them; those never
// appear in filters, and the label maps fall back for an old row.
export const AGENT_NOTE_SOURCES = [
    'sms',
    'client_sms',
    'voice_post_call',
    'sidebar',
    'profile',
    'ui',
    'sync',
    'memory_backfill',
] as const satisfies ReadonlyArray<AgentNoteSource>;
export type LiveAgentNoteSource = (typeof AGENT_NOTE_SOURCES)[number];

export const AGENT_NOTE_DOMAINS = [
    'availability',
    'shift_preference',
    'memory',
    'client_caregiver_preference',
    'attribute',
    'playbook_rule',
    'agency_context',
    'outreach_filter',
] as const satisfies ReadonlyArray<AgentNoteDomain>;
export type LiveAgentNoteDomain = (typeof AGENT_NOTE_DOMAINS)[number];

// "bad" means any thumbs down on the note; "good" means a thumbs up and no
// thumbs down; "unrated" means no rating at all.
export const AGENT_NOTE_RATING_FILTERS = [
    'bad',
    'good',
    'unrated',
] as const satisfies ReadonlyArray<AgentNotesListRating>;
export type AgentNoteRatingFilter = (typeof AGENT_NOTE_RATING_FILTERS)[number];

export const AGENT_NOTE_SORTS = [
    'newest',
    'lowest_confidence',
] as const satisfies ReadonlyArray<
    NonNullable<
        NonNullable<ApiQueryParamsType<'/admin/agent-notes', 'get'>>['sort']
    >
>;
export type AgentNoteSort = (typeof AGENT_NOTE_SORTS)[number];

export const REVIEW_ITEM_STATES = [
    'held',
    'in_progress',
    'dismissed',
] as const satisfies ReadonlyArray<ReviewItemState>;

export const PER_PAGE = 50;

const triStateBooleanSchema = z.preprocess((value) => {
    if (value === 'true') {
        return true;
    }
    if (value === 'false') {
        return false;
    }
    return value;
}, z.boolean().optional().catch(undefined));

export const agentNotesSearchSchema = z.object({
    tab: z.enum(AGENT_NOTE_TABS).catch('notes'),
    // Relative date range; kept relative in the URL so pinned views stay
    // "the last N days". Fixed created_after/created_before take precedence.
    range: z.enum(AGENT_NOTE_RANGE_PRESETS).optional().catch(undefined),
    page: z.coerce.number().int().min(1).catch(1),
    status: z.enum(AGENT_NOTE_STATUSES).optional().catch(undefined),
    source: z.enum(AGENT_NOTE_SOURCES).optional().catch(undefined),
    domain: z.enum(AGENT_NOTE_DOMAINS).optional().catch(undefined),
    rating: z.enum(AGENT_NOTE_RATING_FILTERS).optional().catch(undefined),
    sort: z.enum(AGENT_NOTE_SORTS).optional().catch(undefined),
    low_confidence: triStateBooleanSchema,
    no_new_data: triStateBooleanSchema,
    memory_fallback: triStateBooleanSchema,
    organization_id: z.string().optional(),
    caregiver_id: z.string().optional(),
    client_id: z.string().optional(),
    parked: triStateBooleanSchema,
    park_items: triStateBooleanSchema,
    zero_items: triStateBooleanSchema,
    created_after: z.string().optional(),
    created_before: z.string().optional(),
    search: z.string().optional(),
    note: z.string().optional(),
    // Held items tab filters; the tab treats a missing state as held.
    review_state: z.enum(REVIEW_ITEM_STATES).optional().catch(undefined),
    review_source: z.enum(AGENT_NOTE_SOURCES).optional().catch(undefined),
});

export type AgentNotesSearch = z.infer<typeof agentNotesSearchSchema>;
